'use client'

import React, { createContext, useContext, useMemo, useState, useCallback, useEffect } from 'react'
import { ThemeProvider, createTheme, CssBaseline, Box } from '@mui/material'
import { useReactTable, getCoreRowModel, getSortedRowModel, getFilteredRowModel, getPaginationRowModel, type SortingState, type ColumnFiltersState } from '@tanstack/react-table'
import TableView from './TableView'
import { useCustomTableLogic } from './hooks/useCustomTableLogic'
import { useThemeMode } from './hooks/useThemeMode'
import { useCellEditingOrchestration } from './hooks/useCellEditingOrchestration'
import { THEME_COLORS } from './theme/colors'
import { buildColumns } from './CustomTableColumnsConfig'
import type { ColumnDef, CustomTableProps, TableEditContextType } from './types'

export const TableEditContext = createContext<TableEditContextType | null>(null)

export function useTableEdit() {
  const ctx = useContext(TableEditContext)
  if (!ctx) throw new Error('useTableEdit must be used within CustomTable')
  return ctx
}

export default function CustomTable({
  data: initialData,
  columnsDef,
  onCellUpdate,
  onExportCSV,
  onExportExcel,
  loadingText = 'Cargando datos...',
  noResultsText = 'No se encontraron resultados',
  enableFilters = true,
  enableColumnFilters = true,
  enableSorting = true,
  enablePagination = true,
  enableCellSelection = true,
  enableCellEditing = true,
  enableExport = true,
  defaultPageSize = 100,
  rowHeight = 36,
  containerHeight = '80vh',
  defaultTheme = 'light',
  className,
  style,
}: CustomTableProps) {
  const { isDarkMode, toggleTheme } = useThemeMode(defaultTheme)
  const [data, setData] = useState(initialData)
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [globalFilter, setGlobalFilter] = useState('')
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({})
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({})

  useEffect(() => {
    setData(initialData)
  }, [initialData])

  const columns = useMemo(() => buildColumns(columnsDef, isDarkMode), [columnsDef, isDarkMode])

  const table = useReactTable({
    data,
    columns,
    state: { sorting, columnFilters, globalFilter, columnVisibility },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
    getFilteredRowModel: enableFilters ? getFilteredRowModel() : undefined,
    getPaginationRowModel: enablePagination ? getPaginationRowModel() : undefined,
    initialState: {
      pagination: { pageSize: defaultPageSize },
    },
  })

  const {
    handleConfirmCellEdit,
    isUpdating,
  } = useCellEditingOrchestration({
    data,
    setData,
    onCellUpdate,
  })

  const {
    handleExportCSV,
    handleExportExcel,
  } = useCustomTableLogic({
    data,
    columnsDef,
    table,
    onExportCSV,
    onExportExcel,
  })

  const setColumnWidth = useCallback((colId: string, width: number) => {
    setColumnWidths(prev => ({ ...prev, [colId]: width }))
  }, [])

  const theme = useMemo(() => createTheme({
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
      primary: { main: THEME_COLORS.primary },
      background: {
        default: isDarkMode ? THEME_COLORS.dark.bg : THEME_COLORS.light.bg,
        paper: isDarkMode ? THEME_COLORS.dark.paper : THEME_COLORS.light.paper,
      },
      text: {
        primary: isDarkMode ? THEME_COLORS.dark.text : THEME_COLORS.light.text,
      },
    },
  }), [isDarkMode])

  const editContextValue: TableEditContextType = useMemo(() => ({
    handleConfirmCellEdit,
    isUpdating,
  }), [handleConfirmCellEdit, isUpdating])

  const cssVars = useMemo(() => ({
    '--color-bg': isDarkMode ? THEME_COLORS.dark.bg : THEME_COLORS.light.bg,
    '--color-bg-paper': isDarkMode ? THEME_COLORS.dark.paper : THEME_COLORS.light.paper,
    '--color-text': isDarkMode ? THEME_COLORS.dark.text : THEME_COLORS.light.text,
    '--color-divider': isDarkMode ? THEME_COLORS.dark.divider : THEME_COLORS.light.divider,
    '--color-table-header': isDarkMode ? THEME_COLORS.dark.tableHeader : THEME_COLORS.light.tableHeader,
    '--color-table-index-header': isDarkMode ? THEME_COLORS.dark.tableIndexHeader : THEME_COLORS.light.tableIndexHeader,
    '--color-table-index-body': isDarkMode ? THEME_COLORS.dark.tableIndexBody : THEME_COLORS.light.tableIndexBody,
  } as React.CSSProperties), [isDarkMode])

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <TableEditContext.Provider value={editContextValue}>
        <Box
          className={className}
          style={{ ...cssVars, ...style }}
          sx={{
            height: containerHeight,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'var(--color-bg)',
            color: 'var(--color-text)',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <TableView
            table={table}
            data={data}
            columnsDef={columnsDef}
            columnWidths={columnWidths}
            setColumnWidth={setColumnWidth}
            rowHeight={rowHeight}
            isDarkMode={isDarkMode}
            toggleTheme={toggleTheme}
            enableCellSelection={enableCellSelection}
            enableCellEditing={enableCellEditing}
            enableColumnFilters={enableColumnFilters}
            enableExport={enableExport}
            handleExportCSV={handleExportCSV}
            handleExportExcel={handleExportExcel}
            loadingText={loadingText}
            noResultsText={noResultsText}
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
          />
        </Box>
      </TableEditContext.Provider>
    </ThemeProvider>
  )
}

export * from './types'
export { buildColumns } from './CustomTableColumnsConfig'
export { THEME_COLORS } from './theme/colors'
export { COLUMN_CONFIG, STYLES_CONFIG } from './config'
